/**
 * AgentSheet — agent detail overlay from design/project/lib/dash2.jsx
 * (status + description + send-a-message composer), extended to carry the
 * full agents CRUD the old inline AgentsSection provided: create, edit
 * (machine pin, cwd, description), warm start, and delete. Bottom sheet
 * on phone / centered modal ≥900px via the kit Sheet.
 *
 * Phase 11: existing agents gain an Instructions tab — edit the agent's
 * CLAUDE.md through the plane's daemon round trip (GET/PUT
 * /agents/{name}/file) and view the permission allowlist read-only.
 */
import { useEffect, useState, type SyntheticEvent } from "react";
import { Link } from "react-router-dom";
import {
  AGENT_FILE_MAX_BYTES,
  ApiError,
  PlanLimitError,
  deleteAgent,
  getAgentFile,
  isValidModel,
  machineRuntimes,
  putAgent,
  putAgentFile,
  sendAgentMessage,
  type Agent,
  type Effort,
  type Machine,
  type Runtime,
} from "../lib/api";
import { Sheet, Btn, Toggle, StatusPill, Icon, I } from "../kit";
import { ModelEffortFields } from "./ModelEffortFields";
import { PlanLimitNote } from "./PlanLimitNote";
import { timeAgo } from "../lib/format";

// Contract §1: agent names are [a-z0-9-]{1,64}.
const NAME_RE = /^[a-z0-9-]{1,64}$/;

/** Inline fetch spinner (kit refresh glyph + the .ns-spin animation). */
function Spin({ label }: { label: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "var(--color-fg-3)",
        fontSize: 13,
        padding: "10px 0",
      }}
    >
      <span className="ns-spin" style={{ display: "inline-flex" }}>
        <Icon d={I.refresh} size={14} />
      </span>
      {label}
    </div>
  );
}

/**
 * Instructions tab (phase 11). The plane never touches machine files —
 * GET/PUT round-trip through the daemon on the agent's machine, which must
 * belong to the caller's tenant (tenant rides every call via api.ts; the
 * plane resolves agent → tenant → machine and takes cwd from the agent
 * row, never the request). Mounted lazily on first tab open, then kept
 * mounted (hidden) so tab switches don't refetch or drop edits.
 */
function InstructionsTab({
  agent,
  machineLabel,
}: {
  agent: Agent;
  machineLabel: string;
}) {
  // CLAUDE.md editor — buffer + last daemon-confirmed content.
  const [buf, setBuf] = useState("");
  const [baseline, setBaseline] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<{ code?: string; msg: string } | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  // Read-only permissions viewer (file=settings) — lazy on first expand.
  const [perm, setPerm] = useState<string | null>(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permErr, setPermErr] = useState<string | null>(null);

  const offlineHint =
    agent.machineId === "any"
      ? "no machine is online to read this agent's files — start a daemon, then retry"
      : `${machineLabel} is offline — start its daemon, then retry`;

  const errOf = (e: unknown): { code?: string; msg: string } => {
    if (e instanceof ApiError) {
      if (e.code === "machine_offline" || e.status === 409)
        return { code: "machine_offline", msg: offlineHint };
      if (e.code === "machine_timeout" || e.status === 504)
        return {
          code: "machine_timeout",
          msg: "the machine didn't answer in time",
        };
      if (e.status === 404)
        return { msg: "this plane doesn't support instruction editing yet" };
      return { msg: e.message };
    }
    return { msg: e instanceof Error ? e.message : "request failed" };
  };

  const load = async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const res = await getAgentFile(agent.agentName, "claudemd");
      setBuf(res.content);
      setBaseline(res.content);
      setFetchedAt(res.fetchedAt ?? new Date().toISOString());
    } catch (e) {
      setLoadErr(errOf(e));
    } finally {
      setLoading(false);
    }
  };

  // Lazy GET on open — this component first mounts when the tab is opened.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "saved" flash clears itself.
  useEffect(() => {
    if (!savedFlash) return;
    const t = window.setTimeout(() => setSavedFlash(false), 2500);
    return () => window.clearTimeout(t);
  }, [savedFlash]);

  const bytes = new TextEncoder().encode(buf).length;
  const overCap = bytes > AGENT_FILE_MAX_BYTES;
  const dirty = baseline !== null && buf !== baseline;

  const save = async () => {
    if (saving || !dirty || overCap) return;
    setSaving(true);
    setSaveErr(null);
    try {
      // Contract: PUT returns the daemon-confirmed post-write content —
      // it replaces the buffer so the editor shows what landed on disk.
      const res = await putAgentFile(agent.agentName, buf);
      setBuf(res.content);
      setBaseline(res.content);
      setFetchedAt(res.fetchedAt ?? new Date().toISOString());
      setSavedFlash(true);
    } catch (e) {
      setSaveErr(errOf(e).msg);
    } finally {
      setSaving(false);
    }
  };

  const loadPerm = async () => {
    setPermLoading(true);
    setPermErr(null);
    try {
      const res = await getAgentFile(agent.agentName, "settings");
      setPerm(res.content);
    } catch (e) {
      setPermErr(errOf(e).msg);
    } finally {
      setPermLoading(false);
    }
  };

  const onPermToggle = (e: SyntheticEvent<HTMLDetailsElement>) => {
    if (e.currentTarget.open && perm === null && !permLoading) void loadPerm();
  };

  return (
    <div className="dsh-form" style={{ gap: 14 }}>
      <div>
        <span className="dsh-field-label">CLAUDE.md — instructions</span>
        {loading ? (
          <Spin label={`fetching from ${machineLabel}…`} />
        ) : loadErr ? (
          <div className="dsh-form" style={{ gap: 10 }}>
            <p className="dsh-hint-error" style={{ margin: 0 }}>
              {loadErr.msg}
            </p>
            <Btn variant="outline" onClick={() => void load()}>
              <Icon d={I.refresh} size={13} /> Retry
            </Btn>
          </div>
        ) : (
          <>
            <textarea
              className="input mono"
              rows={16}
              spellCheck={false}
              style={{
                fontSize: 12.5,
                lineHeight: "19px",
                resize: "vertical",
                whiteSpace: "pre",
              }}
              placeholder={
                "# Role\n\nWhat this agent does, its ground rules, where it writes…"
              }
              value={buf}
              onChange={(e) => setBuf(e.target.value)}
            />
            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span
                className="mono"
                style={{ fontSize: 11.5, color: "var(--color-fg-4)" }}
              >
                fetched from {machineLabel} · {timeAgo(fetchedAt)}
              </span>
              {savedFlash && <StatusPill status="delivered">saved</StatusPill>}
            </div>
            {overCap && (
              <p className="dsh-hint-error" style={{ marginTop: 8 }}>
                over the 32KB cap ({Math.ceil(bytes / 1024)}KB) — the daemon
                rejects oversize writes
              </p>
            )}
            {saveErr && (
              <p className="dsh-hint-error" style={{ marginTop: 8 }}>
                {saveErr}
              </p>
            )}
            <Btn
              variant="primary"
              full
              style={{ marginTop: 10 }}
              disabled={saving || !dirty || overCap}
              onClick={() => void save()}
            >
              {saving ? "Saving…" : "Save instructions"}
            </Btn>
          </>
        )}
      </div>

      {/* settings.local.json — read-only in v1 (write_agent_file is
          CLAUDE.md only; grants are managed on the machine). */}
      <details
        onToggle={onPermToggle}
        style={{
          border: "1px solid var(--color-line-2)",
          borderRadius: "var(--rad-2)",
          background: "var(--color-surface-1)",
          padding: "10px 12px",
        }}
      >
        <summary
          style={{ cursor: "pointer", fontSize: 13, color: "var(--color-fg-2)" }}
        >
          Permissions{" "}
          <span
            className="mono"
            style={{ fontSize: 11.5, color: "var(--color-fg-4)" }}
          >
            .claude/settings.local.json
          </span>
        </summary>
        <div className="dsh-form" style={{ gap: 10, marginTop: 10 }}>
          <p className="hint" style={{ margin: 0, fontSize: 12.5 }}>
            Read-only from the web — permission grants are managed on the
            machine itself, so a browser session can never widen what an
            agent may do. Edit the file on {machineLabel} to change grants.
          </p>
          {permLoading ? (
            <Spin label={`fetching from ${machineLabel}…`} />
          ) : permErr ? (
            <p className="dsh-hint-error" style={{ margin: 0 }}>
              {permErr}
            </p>
          ) : perm !== null ? (
            perm.trim() ? (
              <pre
                style={{
                  margin: 0,
                  padding: "10px 12px",
                  background: "var(--color-surface-4)",
                  border: "1px solid var(--color-line-2)",
                  borderRadius: 6,
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  lineHeight: "18px",
                  overflow: "auto",
                  maxHeight: 260,
                }}
              >
                {perm}
              </pre>
            ) : (
              <p className="hint" style={{ margin: 0, fontSize: 12.5 }}>
                no settings file yet — every tool use will prompt
              </p>
            )
          ) : null}
        </div>
      </details>
    </div>
  );
}

export function AgentSheet({
  agent,
  machines,
  onClose,
  onChanged,
}: {
  /** Existing agent, or null to create a new one. */
  agent: Agent | null;
  machines: Machine[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const creating = agent === null;

  // Phase 11: Overview | Instructions subtabs (existing agents only). The
  // Instructions tab mounts lazily on first open, then stays mounted
  // (hidden) so switching back doesn't refetch or lose edits.
  const [view, setView] = useState<"overview" | "instructions">("overview");
  const [instrMounted, setInstrMounted] = useState(false);

  // configure form (PUT /agents/{name} replaces the row)
  const [name, setName] = useState(agent?.agentName ?? "");
  const [description, setDescription] = useState(agent?.description ?? "");
  const [machineId, setMachineId] = useState(agent?.machineId || "any");
  const [cwd, setCwd] = useState(agent?.cwd ?? "");
  const [warm, setWarm] = useState(agent?.warm ?? false);
  // Phase 9 §C: agent-level model/effort defaults ("" = inherit). PUT
  // replaces the row, so clearing back to "" simply omits the field.
  const [defModel, setDefModel] = useState(agent?.defaultModel ?? "");
  const [defEffort, setDefEffort] = useState<"" | Effort>(
    agent?.defaultEffort ?? ""
  );
  // Phase 12 §T9: the coding-agent runtime this agent runs on. Defaults to
  // "claude" for new agents; an existing agent keeps its stored value.
  const [runtime, setRuntime] = useState<Runtime>(agent?.runtime ?? "claude");
  // Session-autoClose agent default. PUT replaces the row, so off (false)
  // simply omits the field, leaving the source default (scheduled/run-now
  // close; ask_agent keep warm). Mirrors `warm`.
  const [defAutoClose, setDefAutoClose] = useState(
    agent?.defaultAutoClose ?? false
  );
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  // Phase 10 §C: PUT /agents/{name} answers 402 plan_limit when CREATING
  // past the free tier's agent count — render the inline upgrade note.
  const [planErr, setPlanErr] = useState<PlanLimitError | null>(null);

  // send-message composer (POST /agents/{name}/message)
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{
    routed: string;
    sessionId: string;
  } | null>(null);

  const machineName = (id: string) =>
    id === "any"
      ? "any machine"
      : (machines.find((m) => m.machineId === id)?.name ?? id.slice(0, 8));

  // Phase 12 §T9: only offer codex/any when SOME machine actually reports
  // codex — never advertise a runtime nothing can serve. An agent already
  // set to codex/any (elsewhere) still shows its current value even when no
  // machine reports codex, so we never silently drop it.
  const codexAvailable = machines.some((m) =>
    machineRuntimes(m).includes("codex")
  );
  const showRuntimeOptions = codexAvailable || runtime !== "claude";

  const save = async () => {
    if (busy) return;
    const n = (creating ? name : agent.agentName).trim();
    if (!NAME_RE.test(n)) {
      setFormErr("name must be 1–64 chars of a-z, 0-9, hyphen");
      return;
    }
    // Contract §C: presets or a raw model id (/^[a-z0-9.-]{3,64}$/).
    if (defModel && !isValidModel(defModel)) {
      setFormErr(
        "model must be opus/sonnet/haiku or a raw id (a-z, 0-9, dot, hyphen)"
      );
      return;
    }
    setBusy(true);
    setFormErr(null);
    setPlanErr(null);
    try {
      await putAgent(n, {
        machineId,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(cwd.trim() ? { cwd: cwd.trim() } : {}),
        ...(warm ? { warm: true } : {}),
        // Phase 9 §C: omitted when "inherit" — the row-replace PUT clears
        // any stored default.
        ...(defModel ? { defaultModel: defModel } : {}),
        ...(defEffort ? { defaultEffort: defEffort } : {}),
        // Session-autoClose: omitted when off — the row-replace PUT clears
        // any stored default back to the source default.
        ...(defAutoClose ? { defaultAutoClose: true } : {}),
        // Phase 12 §T9: always send the runtime the select carries — the
        // plane SETs it when present (no row-replace clear for runtime).
        runtime,
      });
      onChanged();
      onClose();
    } catch (e) {
      if (e instanceof PlanLimitError) {
        setPlanErr(e); // inline upgrade note, no modal
      } else {
        setFormErr(e instanceof Error ? e.message : "save failed");
      }
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!agent || deleting) return;
    if (
      !window.confirm(
        `Delete agent "${agent.agentName}"? Running sessions are not touched.`
      )
    )
      return;
    setDeleting(true);
    setFormErr(null);
    try {
      await deleteAgent(agent.agentName);
      onChanged();
      onClose();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : "delete failed");
      setDeleting(false);
    }
  };

  const send = async () => {
    if (!agent || sending) return;
    const text = msg.trim();
    if (!text) return;
    setSending(true);
    setSendErr(null);
    setSent(null);
    try {
      const res = await sendAgentMessage(agent.agentName, text);
      setSent({ routed: res.routed, sessionId: res.sessionId });
      setMsg("");
      onChanged();
    } catch (e) {
      setSendErr(
        e instanceof ApiError && e.code === "machine_offline"
          ? "machine offline — start the daemon or pin the agent to an online machine"
          : e instanceof Error
            ? e.message
            : "send failed"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet title={creating ? "New agent" : agent.agentName} onClose={onClose}>
      <div className="dsh-form" style={{ gap: 16 }}>
        {!creating && (
          <div className="dsh-subtabs" role="tablist" style={{ marginBottom: 0 }}>
            <button
              type="button"
              role="tab"
              aria-selected={view === "overview"}
              className={view === "overview" ? "on" : undefined}
              onClick={() => setView("overview")}
            >
              Overview
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === "instructions"}
              className={view === "instructions" ? "on" : undefined}
              onClick={() => {
                setView("instructions");
                setInstrMounted(true);
              }}
            >
              Instructions
            </button>
          </div>
        )}

        {!creating && view === "overview" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <StatusPill status={agent.currentSessionId ? "working" : "idleS"} />
              <span className="mono" style={{ fontSize: 11.5, color: "var(--color-fg-4)" }}>
                on {machineName(agent.machineId)}
              </span>
              {agent.currentSessionId && (
                <Link
                  className="dsh-link"
                  to={`/sessions/${agent.currentSessionId}`}
                >
                  open session →
                </Link>
              )}
            </div>
            {agent.description && (
              <p
                style={{
                  fontSize: 13.5,
                  color: "var(--color-fg-2)",
                  lineHeight: "20px",
                  margin: 0,
                }}
              >
                {agent.description}
              </p>
            )}

            {/* composer — the core routing primitive */}
            <div>
              <span className="dsh-field-label">Send a message</span>
              <textarea
                className="input"
                rows={3}
                placeholder="Ask the agent to do something now…"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
              />
              {sent ? (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <StatusPill status="queued">
                    routed to {sent.routed} session
                  </StatusPill>
                  <Link className="dsh-link" to={`/sessions/${sent.sessionId}`}>
                    view session →
                  </Link>
                </div>
              ) : (
                <Btn
                  variant="primary"
                  full
                  style={{ marginTop: 10 }}
                  disabled={sending || !msg.trim()}
                  onClick={send}
                >
                  <Icon d={I.send} size={14} />{" "}
                  {sending ? "Sending…" : "Send to agent"}
                </Btn>
              )}
              {sendErr && (
                <p className="dsh-hint-error" style={{ marginTop: 8 }}>
                  {sendErr}
                </p>
              )}
            </div>
          </>
        )}

        {/* configure (create or edit) */}
        {(creating || view === "overview") && (
        <div className="dsh-form" style={{ gap: 14 }}>
          {!creating && <span className="dsh-field-label">Configure</span>}
          {creating && (
            <div>
              <span className="dsh-field-label">Name</span>
              <input
                className="input mono"
                placeholder="research-bot"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}
          <div>
            <span className="dsh-field-label">Machine</span>
            <select
              className="input"
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
            >
              <option value="any">any</option>
              {machines.map((m) => (
                <option key={m.machineId} value={m.machineId}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="dsh-field-label">Working directory (optional)</span>
            <input
              className="input mono"
              placeholder="~/dev/project"
              value={cwd}
              onChange={(e) => setCwd(e.target.value)}
            />
          </div>
          <div>
            <span className="dsh-field-label">Description (optional)</span>
            <input
              className="input"
              placeholder="what this agent is for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {/* phase 9 §C: agent defaults — schedules/run-now override per run */}
          <ModelEffortFields
            model={defModel}
            effort={defEffort}
            onModel={setDefModel}
            onEffort={setDefEffort}
            modelLabel="Default model"
            effortLabel="Default effort"
            hint='defaults for every run of this agent — a schedule or run-now override wins; "inherit" leaves Claude Code on its own defaults'
          />

          {/* phase 12 §T9: coding-agent runtime. codex/any only appear when
              a machine actually reports codex — except we always keep the
              agent's current value selectable so an existing codex/any agent
              isn't silently downgraded on a Claude-only swarm. */}
          <div>
            <label className="dsh-field-label" htmlFor="agent-runtime">
              Runtime
            </label>
            <select
              id="agent-runtime"
              className="input"
              value={runtime}
              onChange={(e) => setRuntime(e.target.value as Runtime)}
            >
              <option value="claude">claude</option>
              {/* codex/any appear when a machine reports codex; an agent
                  already set to codex/any keeps its value selectable via
                  showRuntimeOptions (runtime !== "claude"). */}
              {showRuntimeOptions && <option value="codex">codex</option>}
              {showRuntimeOptions && <option value="any">any</option>}
            </select>
            <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
              {codexAvailable
                ? 'the coding agent this runs on — "any" lets the swarm pick a machine that serves either'
                : runtime === "claude"
                  ? "no machine reports codex yet — enroll a codex-capable machine to unlock it"
                  : "no machine currently reports codex; this agent's runtime is kept as set"}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Toggle
              on={warm}
              onClick={() => setWarm((w) => !w)}
              aria-label={warm ? "disable warm start" : "enable warm start"}
              title="warm — auto-starts a session when its machine's daemon connects"
            />
            <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
              warm start
              <span className="hint" style={{ display: "block", fontSize: 12 }}>
                auto-start a session when the machine's daemon connects
              </span>
            </span>
          </div>

          {/* session-autoClose: the agent's default close-on-complete. A
              schedule or run-now override beats this; ask_agent always keeps
              its rolling session warm regardless. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Toggle
              on={defAutoClose}
              onClick={() => setDefAutoClose((v) => !v)}
              aria-label={
                defAutoClose
                  ? "disable default auto-close"
                  : "enable default auto-close"
              }
              title="auto-close — end the session once it finishes its turn (fresh next run)"
            />
            <span style={{ fontSize: 13, color: "var(--color-fg-2)" }}>
              Close sessions when the task finishes (start fresh next run)
              <span className="hint" style={{ display: "block", fontSize: 12 }}>
                agent default — a schedule or run-now override wins. Scheduled
                and run-now work already close by default; an interactive ask
                always keeps its session warm
              </span>
            </span>
          </div>

          {formErr && <span className="dsh-hint-error">{formErr}</span>}
          {planErr && <PlanLimitNote err={planErr} />}

          <div className="dsh-form-actions">
            {!creating && (
              <Btn variant="danger" onClick={remove} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </Btn>
            )}
            <Btn variant="outline" full onClick={onClose}>
              Cancel
            </Btn>
            <Btn
              variant="primary"
              full
              onClick={save}
              disabled={busy || (creating && !name.trim())}
            >
              {busy ? "Saving…" : creating ? "Create agent" : "Save changes"}
            </Btn>
          </div>
        </div>
        )}

        {/* instructions — kept mounted once visited (display:contents lets
            its content sit directly in the outer dsh-form flex flow) */}
        {!creating && instrMounted && (
          <div
            style={{
              display: view === "instructions" ? "contents" : "none",
            }}
          >
            <InstructionsTab
              agent={agent}
              machineLabel={machineName(agent.machineId)}
            />
          </div>
        )}
      </div>
    </Sheet>
  );
}
